import type { Letter } from "@repo/hindi-shared";
import { letterById, mulberry32, pickDistractors } from "@repo/hindi-shared";
import { useEffect, useMemo, useRef, useState } from "react";

import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";

import { gradeFromChoice } from "./grading";
import { dateKey, hashSeed } from "./seed";
import type { ExerciseProps } from "./types";

const CORRECT_FLASH_MS = 450;

/** Fisher-Yates shuffle using an injected `[0, 1)` rand source. Non-mutating. */
function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = copy[i]!;
    copy[i] = copy[j]!;
    copy[j] = tmp;
  }
  return copy;
}

/**
 * SymbolToSound — the glyph is shown; the learner picks its sound from 4
 * text options (IAST + hint for the target and 3 seeded distractors). After
 * answering, the target's audio plays again as reinforcement.
 */
export function SymbolToSound({ exercise, audio, onComplete }: ExerciseProps) {
  const letter = letterById.get(exercise.itemId);

  const optionIds = useMemo(() => {
    if (letter === undefined) return [];
    const rand = mulberry32(hashSeed(exercise.itemId + dateKey()));
    const distractors = pickDistractors(exercise.itemId, 3, rand);
    return shuffle([exercise.itemId, ...distractors], rand);
  }, [exercise.itemId, letter]);

  const [attempts, setAttempts] = useState(0);
  const [wrongIds, setWrongIds] = useState<Set<string>>(new Set());
  const [correctId, setCorrectId] = useState<string | null>(null);

  const cancelledRef = useRef(false);
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  if (letter === undefined) {
    return null;
  }

  const audioText = letter.audioText;

  function handlePick(pickedId: string): void {
    if (correctId !== null) return; // already answered

    const nextAttempts = attempts + 1;
    setAttempts(nextAttempts);

    if (pickedId === exercise.itemId) {
      setCorrectId(pickedId);
      void audio.speak(audioText);
      const grade = gradeFromChoice(nextAttempts);
      window.setTimeout(() => {
        if (cancelledRef.current) return;
        onComplete({ grade, itemId: exercise.itemId });
      }, CORRECT_FLASH_MS);
    } else {
      setWrongIds((prev) => new Set(prev).add(pickedId));
    }
  }

  function optionState(id: string): "idle" | "correct" | "wrong" {
    if (correctId === id) return "correct";
    if (wrongIds.has(id)) return "wrong";
    return "idle";
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <DualLabel
        en="How does this letter sound? Choose the sound."
        hi="यह अक्षर कैसे बोलता है? आवाज़ चुनें"
        className="font-latin text-sm text-ink-soft"
      />

      <GlyphTile glyph={letter.glyph} size="lg" disabled />

      <div className="flex flex-col gap-3">
        {optionIds.map((id) => {
          const optionLetter: Letter | undefined = letterById.get(id);
          if (optionLetter === undefined) return null;
          const state = optionState(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => handlePick(id)}
              disabled={correctId !== null || wrongIds.has(id)}
              className={`border px-6 py-2.5 font-latin text-base transition-colors duration-150 disabled:cursor-not-allowed ${
                state === "correct"
                  ? "border-sindoor text-sindoor"
                  : state === "wrong"
                    ? "border-ink-soft text-ink-soft line-through"
                    : "border-rule text-ink hover:border-ink"
              }`}
            >
              {optionLetter.iast} — {optionLetter.hint}
            </button>
          );
        })}
      </div>
    </div>
  );
}
